/**
 * Accounts Service Module
 * 
 * This service handles all API interactions related to user accounts and positions.
 * It provides methods for fetching account data, managing portfolios, and refreshing
 * information from the backend API.
 * 
 * Key features:
 * - Authenticated API requests with JWT tokens
 * - Account listing and detailed information
 * - Real-time balance and position updates
 * - Portfolio overview with summary statistics
 * - Manual data refresh and synchronization
 * - Error handling with structured responses
 * 
 * Authentication:
 * All requests use the centralized API client which handles token management,
 * automatic refresh, and authentication headers.
 */

import type { Account, Position } from '@trading-app/shared';
import { apiClient } from './api-client';

class AccountsService {
  /**
   * Fetch all accounts associated with the authenticated user
   * 
   * Retrieves cached account data from the backend database. This provides
   * fast access to account information without calling external APIs.
   * 
   * @returns Promise resolving to array of user accounts
   */
  async getAccounts(): Promise<Account[]> {
    console.log('🏦 Fetching accounts from /api/accounts');
    try {
      const response = await apiClient.get('/api/accounts');
      console.log('📡 Accounts API response status:', response.status, response.statusText);
      const data = await apiClient.handleResponse<{ success: boolean; accounts: Account[] }>(response);
      console.log('✅ Accounts fetched:', data.accounts.length);
      return data.accounts;
    } catch (error) {
      console.error('❌ Get accounts error:', error);
      throw error;
    }
  }

  /**
   * Fetch detailed information for a specific account
   * 
   * Retrieves comprehensive account data including balance, type, status,
   * and synchronization information for the specified account.
   * 
   * @param accountId - Unique identifier for the account
   * @returns Promise resolving to complete account information
   */
  async getAccountDetails(accountId: string): Promise<Account> {
    const response = await apiClient.get(`/api/accounts/${accountId}`);
    const data = await apiClient.handleResponse<{ success: boolean; account: Account }>(response);
    return data.account;
  }

  /**
   * Fetch fresh account balance from brokerage via SnapTrade
   * 
   * Triggers a real-time balance update by calling the SnapTrade API directly.
   * This provides the most current balance information but may be slower than
   * cached data due to external API calls.
   * 
   * @param accountId - Unique identifier for the account
   * @returns Promise resolving to current balance and last sync timestamp
   */
  async getAccountBalance(accountId: string): Promise<{ balance: number; lastSyncAt: Date | null }> {
    const response = await apiClient.get(`/api/accounts/${accountId}/balance`);
    const data = await apiClient.handleResponse<{ 
      success: boolean; 
      balance: number; 
      lastSyncAt: string | null 
    }>(response);

    return {
      balance: data.balance,
      lastSyncAt: data.lastSyncAt ? new Date(data.lastSyncAt) : null,
    };
  }

  /**
   * Fetch all positions/holdings for a specific account
   * 
   * Retrieves cached position data including stock symbols, quantities,
   * current prices, and profit/loss calculations. Data is from the most
   * recent synchronization with the brokerage.
   * 
   * @param accountId - Unique identifier for the account
   * @returns Promise resolving to array of position objects
   */
  async getAccountPositions(accountId: string): Promise<Position[]> {
    const response = await apiClient.get(`/api/accounts/${accountId}/positions`);
    const data = await apiClient.handleResponse<{ success: boolean; positions: Position[] }>(response);
    return data.positions;
  }

  /**
   * Manually refresh account data from brokerage
   * 
   * Triggers fresh data retrieval for both account balance and positions
   * from the connected brokerage via SnapTrade API. Updates the cached
   * data in our database.
   * 
   * @param accountId - Unique identifier for the account to refresh
   * @returns Promise that resolves when refresh is complete
   */
  async refreshAccount(accountId: string): Promise<void> {
    const response = await apiClient.post(`/api/accounts/${accountId}/refresh`);
    await apiClient.handleResponse<{ success: boolean; message: string }>(response);
  }

  /**
   * Fetch complete portfolio overview from cached database
   * 
   * Retrieves comprehensive portfolio data including all accounts, positions,
   * and calculated summary statistics. This is optimized for performance by
   * reading from our database cache rather than making external API calls.
   * 
   * Portfolio summary includes:
   * - Total account count and balance
   * - Total position count and market value
   * - Overall profit/loss calculations
   * 
   * @returns Promise resolving to complete portfolio data with summary
   */
  async getPortfolio(): Promise<{
    accounts: Account[];
    positions: Position[];
    summary: {
      totalAccounts: number;
      totalPositions: number;
      totalMarketValue: number;
      totalUnrealizedPnL: number;
      totalBalance: number;
    };
  }> {
    const response = await apiClient.get('/api/accounts/portfolio');
    const data = await apiClient.handleResponse<{
      success: boolean;
      accounts: Account[];
      positions: Position[];
      summary: {
        totalAccounts: number;
        totalPositions: number;
        totalMarketValue: number;
        totalUnrealizedPnL: number;
        totalBalance: number;
      };
    }>(response);

    return {
      accounts: data.accounts,
      positions: data.positions,
      summary: data.summary,
    };
  }

  /**
   * Synchronize all user accounts from SnapTrade
   * 
   * Performs a comprehensive sync of all connected accounts and their positions
   * from the SnapTrade API to our database. This is typically called after a
   * successful brokerage connection or for periodic data updates.
   * 
   * @returns Promise resolving to sync statistics (accounts and positions synced)
   */
  async syncAllAccounts(): Promise<{ accountsSynced: number; positionsSynced: number }> {
    const response = await apiClient.post('/api/accounts/sync');
    const data = await apiClient.handleResponse<{ 
      success: boolean; 
      message: string; 
      accountsSynced: number; 
      positionsSynced: number;
    }>(response);
    
    return {
      accountsSynced: data.accountsSynced,
      positionsSynced: data.positionsSynced,
    };
  }

  /**
   * Remove an account (soft-delete)
   * 
   * Marks the account as inactive while preserving all historical data
   * (trades, positions) for audit trail purposes. The account will be
   * filtered from all future queries but remain in the database.
   * 
   * @param accountId - Unique identifier for the account to remove
   * @returns Promise that resolves when removal is complete
   */
  async removeAccount(accountId: string): Promise<void> {
    const response = await apiClient.delete(`/api/accounts/${accountId}`);
    await apiClient.handleResponse<{ success: boolean; message: string }>(response);
  }

  /**
   * Fetch all open positions from positions table
   * 
   * Retrieves all current positions across all user accounts from the
   * positions table. This reflects what's actually at the broker after sync.
   * 
   * @returns Promise resolving to array of open position objects
   */
  async getOpenPositions(): Promise<Array<{
    id: string;
    ticker: string;
    accountId: string;
    accountName: string;
    brokerageName: string;
    quantity: number;
    averageCost: number;
    currentPrice: number;
    marketValue: number;
    unrealizedPnL: number;
    lastUpdatedAt: Date;
  }>> {
    const response = await apiClient.get('/api/accounts/open-positions');
    const data = await apiClient.handleResponse<{
      success: boolean;
      positions: Array<{
        id: string;
        ticker: string;
        accountId: string;
        accountName: string;
        brokerageName: string;
        quantity: number;
        averageCost: number;
        currentPrice: number;
        marketValue: number;
        unrealizedPnL: number;
        lastUpdatedAt: string;
      }>;
      total: number;
    }>(response);

    // Transform lastUpdatedAt string to Date
    return data.positions.map(pos => ({
      ...pos,
      lastUpdatedAt: new Date(pos.lastUpdatedAt),
    }));
  }

  /**
   * Fetch historical balance data for charting
   * 
   * Retrieves balance history from the balance_ledger table, aggregated
   * by hour or day for performance. Supports filtering by specific account
   * or aggregate across all user accounts.
   * 
   * @param params - Query parameters for the balance history request
   * @param params.accountId - Optional: filter by specific account ID
   * @param params.startDate - ISO date string for range start
   * @param params.endDate - ISO date string for range end
   * @param params.granularity - Aggregation level: 'hour' or 'day'
   * @returns Promise resolving to balance history data points
   */
  async getBalanceHistory(params: {
    accountId?: string;
    startDate: string;
    endDate: string;
    granularity?: 'hour' | 'day';
  }): Promise<{ timestamp: Date; value: number }[]> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
      granularity: params.granularity || 'hour',
    });

    if (params.accountId) {
      queryParams.append('accountId', params.accountId);
    }

    console.log('🔍 Fetching balance history:', {
      url: `/api/accounts/balance-history?${queryParams.toString()}`,
      params,
    });

    try {
      const response = await apiClient.get(`/api/accounts/balance-history?${queryParams.toString()}`);
      console.log('📡 API response status:', response.status, response.statusText);
      
      const data = await apiClient.handleResponse<{
        success: boolean;
        data: Array<{ timestamp: string; balance: number }>;
        accountIds: string[];
      }>(response);

    console.log('✅ Balance history response:', {
      success: data.success,
      dataPoints: data.data.length,
      accountIds: data.accountIds,
      firstPoint: data.data[0],
      lastPoint: data.data[data.data.length - 1],
    });

    // Transform API response to match chart format
    const transformed = data.data.map(point => ({
      timestamp: new Date(point.timestamp),
      value: point.balance,
    }));
    
      console.log('🔄 Transformed data:', {
        length: transformed.length,
        firstPoint: transformed[0],
        lastPoint: transformed[transformed.length - 1],
      });

      return transformed;
    } catch (error) {
      console.error('❌ Balance history error:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        url: `/api/accounts/balance-history?${queryParams.toString()}`,
        params,
      });
      throw error;
    }
  }
}

// Export singleton instance of the accounts service for use throughout the app
export const accountsService = new AccountsService();